import { useCan } from '@features/auth';
import { absoluteTime, relativeTime } from '@lib/time';
import {
    Badge,
    Button,
    Card,
    CardHeader,
    EmptyState,
    ErrorState,
    IconArrowLeft,
    IconPlus,
    KeyValue,
    LoadingRows,
    PropagationNotice,
    Toggle,
    WriteError,
} from '@shared/ui';
import { useState } from 'react';
import { Link } from 'react-router';

import { defaultWriteFrom } from '../api/default-write';
import { useClearOverride, useFlag, useSetDefault, useSetOverride } from '../hooks/use-flags';
import { FLAG_TYPE_LABEL, FLAG_TYPE_PARAM, FLAG_TYPE_TONE } from '../schemas/flag-type';
import { LIFECYCLE_LABEL, LIFECYCLE_TONE } from '../schemas/lifecycle';

import { EditFlagDialog } from './EditFlagDialog';
import { FlagHistory } from './FlagHistory';
import { OverrideDialog } from './OverrideDialog';
import { type PlatformDefaultChange } from './platform-default-confirm-copy';
import { PlatformDefaultConfirmDialog } from './PlatformDefaultConfirmDialog';
import { ReadOnlyNotice } from './ReadOnlyNotice';
import { offersRollout } from './rollout-ui';
import { VerdictBadge } from './VerdictBadge';

import type { FlagDetail, FlagOverrideView, FlagType } from '../schemas/flag';

export function FlagDetailPage({ flagKey }: { flagKey: string }) {
    const flag = useFlag(flagKey);
    // Back to the tab this flag lives on, once we know which one that is.
    const backTo =
        flag.data !== undefined
            ? `/flags?${FLAG_TYPE_PARAM}=${flag.data.definition.type}`
            : '/flags';

    return (
        <>
            <Link
                to={backTo}
                className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-content-muted hover:text-accent-icon"
            >
                <IconArrowLeft className="size-4" /> Feature Flags
            </Link>

            {flag.isPending ? (
                <Card>
                    <LoadingRows label="Loading flag…" />
                </Card>
            ) : flag.isError ? (
                <Card>
                    <ErrorState error={flag.error} onRetry={() => void flag.refetch()} />
                </Card>
            ) : (
                <Loaded flagKey={flagKey} detail={flag.data} />
            )}
        </>
    );
}

function Loaded({ flagKey, detail }: { flagKey: string; detail: FlagDetail }) {
    const { definition, default: current, overrides, history } = detail;
    const setDefault = useSetDefault(flagKey);
    const [overriding, setOverriding] = useState(false);
    const [editing, setEditing] = useState(false);
    // NOXSCRUM-1529: a platform-default write (either toggle below shares the
    // same `PUT /default` route and blast radius) is held here, unwritten,
    // until the operator confirms in `PlatformDefaultConfirmDialog`.
    const [pendingDefaultChange, setPendingDefaultChange] = useState<PlatformDefaultChange | null>(
        null,
    );
    // Mirrors the two tiers on the API: one museum is the Ops-CSM job, the
    // platform default is engineer+ because it moves every museum at once.
    // Editing the definition is `ops-csm` too (`PATCH /api/flags/:key`).
    const canOverride = useCan('ops-csm');
    const canEdit = useCan('ops-csm');
    const canSetDefault = useCan('engineer');
    const isSalesforce = definition.type === 'salesforce';

    return (
        <>
            <ReadOnlyNotice />

            <div className="mb-6 flex items-start gap-4">
                <div className="min-w-0">
                    <div className="mb-1 flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-accent-icon">
                            Feature Flag
                        </span>
                        <Badge tone={FLAG_TYPE_TONE[definition.type]}>
                            {FLAG_TYPE_LABEL[definition.type]}
                        </Badge>
                    </div>
                    <h1 className="break-all font-mono text-[22px] font-semibold tracking-tight text-content">
                        {definition.key}
                    </h1>
                    <p className="mt-1 max-w-2xl text-[13.5px] text-content-muted">
                        {definition.description}
                    </p>
                </div>
                <div className="ml-auto flex shrink-0 gap-2.5">
                    <Button
                        variant="ghost"
                        disabled={!canEdit}
                        onClick={() => {
                            setEditing(true);
                        }}
                    >
                        Edit details
                    </Button>
                    <Button
                        variant="ghost"
                        disabled={!canOverride}
                        onClick={() => {
                            setOverriding(true);
                        }}
                    >
                        <IconPlus className="size-4" />
                        Override a museum
                    </Button>
                </div>
            </div>

            {current.archived && (
                <div
                    role="status"
                    className="mb-5 rounded-lg border border-warning-line bg-warning-surface px-4 py-3 text-[13px] leading-relaxed text-warning-strong"
                >
                    <strong className="font-semibold">This flag is archived.</strong> It resolves{' '}
                    <code className="font-mono">false</code> everywhere — including for the museums
                    listed below that still carry an override. Un-archive it before any of those
                    values mean anything.
                </div>
            )}

            <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                {/* `min-w-0` is load-bearing: a grid track defaults to
                    `min-width: auto`, so the override table below would size the
                    column to its own content and shove the 320px sidecar off the
                    right edge of the page. The table has its own horizontal
                    scroll; the page must not. */}
                <div className="flex min-w-0 flex-col gap-5">
                    {/* ---------------------------------------- platform default - */}
                    <Card>
                        <CardHeader
                            title="Platform default"
                            subtitle="Every museum without an override"
                        />
                        <div className="flex flex-col gap-3.5 px-5 py-4">
                            <div className="flex items-center gap-3">
                                <Toggle
                                    on={current.enabled}
                                    busy={setDefault.isPending}
                                    disabled={current.archived}
                                    showLabel
                                    onChange={
                                        canSetDefault
                                            ? (next) => {
                                                  setPendingDefaultChange({
                                                      field: 'enabled',
                                                      next,
                                                  });
                                              }
                                            : undefined
                                    }
                                    aria-label={`Platform default for ${definition.key}`}
                                />
                                {isSalesforce ? (
                                    <span className="text-[13px] text-content-muted">
                                        Projected to every org without an override, as{' '}
                                        {definition.salesforceField ?? 'its field'}.
                                    </span>
                                ) : (
                                    offersRollout(definition.type) && (
                                        <span className="text-[13px] text-content-muted">
                                            {current.rolloutPercentage === undefined
                                                ? 'No rollout gate — on means on for everyone.'
                                                : `Gated at ${String(current.rolloutPercentage)}% of users.`}
                                        </span>
                                    )
                                )}
                                <span className="ml-auto text-[12px] text-content-faint">
                                    v{current.version} · {current.updatedBy}{' '}
                                    <span title={absoluteTime(current.updatedAt)}>
                                        {relativeTime(current.updatedAt)}
                                    </span>
                                </span>
                            </div>

                            <div className="flex items-center gap-3 border-t border-line-subtle pt-3.5">
                                <Toggle
                                    on={current.archived}
                                    busy={setDefault.isPending}
                                    // Archiving rides the same `PUT /default`
                                    // route, so it carries the same tier.
                                    onChange={
                                        canSetDefault
                                            ? (next) => {
                                                  setPendingDefaultChange({
                                                      field: 'archived',
                                                      next,
                                                  });
                                              }
                                            : undefined
                                    }
                                    aria-label={`Archived state for ${definition.key}`}
                                />
                                <span className="text-[13px] text-content-muted">
                                    <strong className="font-semibold text-content-body">
                                        Archived
                                    </strong>{' '}
                                    — the real kill switch. Overrides an override.
                                </span>
                            </div>

                            <PropagationNotice />
                        </div>
                    </Card>

                    {/* -------------------------------------------- override rows - */}
                    <Card>
                        <CardHeader
                            title="Museums overriding this flag"
                            subtitle={`${String(overrides.length)} of them`}
                        />
                        {overrides.length === 0 ? (
                            <EmptyState
                                title="No museum overrides this flag"
                                description="Every museum resolves the platform default above."
                                action={
                                    <Button
                                        variant="ghost"
                                        disabled={!canOverride}
                                        onClick={() => {
                                            setOverriding(true);
                                        }}
                                    >
                                        <IconPlus className="size-4" />
                                        Override a museum
                                    </Button>
                                }
                            />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-surface-sunken text-left text-[11px] uppercase tracking-wide text-content-muted">
                                            <th className="px-5 py-3 font-semibold">Museum</th>
                                            <th className="px-5 py-3 font-semibold">
                                                Stored value
                                            </th>
                                            <th className="px-5 py-3 font-semibold">Resolves to</th>
                                            {offersRollout(definition.type) && (
                                                <th className="px-5 py-3 font-semibold">Rollout</th>
                                            )}
                                            <th className="px-5 py-3" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overrides.map((view) => (
                                            <OverrideRow
                                                key={view.override.tenantId}
                                                flagKey={flagKey}
                                                flagType={definition.type}
                                                view={view}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </div>

                {/* ----------------------------------- definition + history sidecar - */}
                <div className="flex flex-col gap-4">
                    <Card>
                        <CardHeader title="Definition" />
                        <div className="px-5 py-2">
                            <KeyValue label="Type">
                                <Badge tone={FLAG_TYPE_TONE[definition.type]}>
                                    {FLAG_TYPE_LABEL[definition.type]}
                                </Badge>
                            </KeyValue>
                            {isSalesforce && (
                                <KeyValue label="Salesforce field">
                                    <span
                                        className="font-mono text-[12px]"
                                        title={definition.salesforceField}
                                    >
                                        {definition.salesforceField}
                                    </span>
                                </KeyValue>
                            )}
                            <KeyValue label="Name">{definition.name}</KeyValue>
                            <KeyValue label="Category">{definition.category}</KeyValue>
                            <KeyValue label="Lifecycle">
                                <Badge tone={LIFECYCLE_TONE[definition.lifecycle]}>
                                    {LIFECYCLE_LABEL[definition.lifecycle]}
                                </Badge>
                            </KeyValue>
                            <KeyValue label="Owner team">{definition.owner}</KeyValue>
                            <KeyValue label="Created by">{definition.createdBy}</KeyValue>
                            <KeyValue label="Created">
                                <span title={absoluteTime(definition.createdAt)}>
                                    {relativeTime(definition.createdAt)}
                                </span>
                            </KeyValue>
                        </div>
                    </Card>

                    <Card>
                        <CardHeader title="Change history" subtitle="Most recent first" />
                        <FlagHistory history={history} />
                    </Card>
                </div>
            </div>

            <OverrideDialog
                flagKey={flagKey}
                kind={definition}
                open={overriding}
                onClose={() => {
                    setOverriding(false);
                }}
            />

            <EditFlagDialog
                definition={definition}
                open={editing}
                onClose={() => {
                    setEditing(false);
                }}
            />

            <PlatformDefaultConfirmDialog
                flagKey={flagKey}
                kind={definition}
                pending={pendingDefaultChange}
                isPending={setDefault.isPending}
                error={setDefault.error}
                onCancel={() => {
                    setDefault.reset();
                    setPendingDefaultChange(null);
                }}
                onConfirm={() => {
                    if (!pendingDefaultChange) return;
                    setDefault.mutate(
                        defaultWriteFrom(current, {
                            [pendingDefaultChange.field]: pendingDefaultChange.next,
                        }),
                        { onSuccess: () => setPendingDefaultChange(null) },
                    );
                }}
            />
        </>
    );
}

/**
 * One overriding museum.
 *
 * Shows the **stored value** and the **resolved verdict** as separate columns
 * rather than one switch. They disagree more often than is comfortable: an
 * override of `true` on an archived flag resolves `false`, and so does one
 * behind a 0% rollout. A single toggle would have to pick one of the two to
 * display, and either choice tells an operator something untrue half the time.
 *
 * A `salesforce` flag has no rollout column — the API refuses a percentage on
 * one (D6), so the column could only ever read "No gate".
 */
function OverrideRow({
    flagKey,
    flagType,
    view,
}: {
    flagKey: string;
    flagType: FlagType;
    view: FlagOverrideView;
}) {
    const { override, verdict } = view;
    const setOverride = useSetOverride(flagKey);
    const clearOverride = useClearOverride(flagKey);
    const busy = setOverride.isPending || clearOverride.isPending;
    const error = setOverride.error ?? clearOverride.error;
    // One tier for both controls. Reverting a museum to a default that is `on`
    // *enables* something for a customer, so it is not the gentler of the two.
    const canOverride = useCan('ops-csm');
    const showRollout = offersRollout(flagType);

    return (
        <>
            <tr className="border-t border-line-subtle text-[13.5px] hover:bg-surface-sunken">
                <td className="px-5 py-3">
                    <div className="font-mono text-xs text-content-strong">{override.tenantId}</div>
                </td>
                <td className="px-5 py-3">
                    <Toggle
                        on={override.enabled}
                        busy={busy}
                        showLabel
                        onChange={
                            canOverride
                                ? (next) => {
                                      setOverride.mutate({
                                          tenantId: override.tenantId,
                                          enabled: next,
                                          // Round-trip the rollout. Sending only
                                          // `enabled` would silently promote a
                                          // 25% gate to 100% for this museum,
                                          // and nothing downstream could tell
                                          // that apart from someone meaning it.
                                          ...(override.rolloutPercentage !== undefined
                                              ? { rolloutPercentage: override.rolloutPercentage }
                                              : {}),
                                      });
                                  }
                                : undefined
                        }
                        aria-label={`${flagKey} for ${override.tenantId}`}
                    />
                </td>
                <td className="px-5 py-3">
                    <VerdictBadge verdict={verdict} />
                </td>
                {showRollout && (
                    <td className="px-5 py-3 text-content-body">
                        {override.rolloutPercentage === undefined ? (
                            <span className="text-xs text-content-faint">No gate</span>
                        ) : (
                            <span className="font-semibold">{override.rolloutPercentage}%</span>
                        )}
                    </td>
                )}
                <td className="px-5 py-3 text-right">
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !canOverride}
                        onClick={() => {
                            clearOverride.mutate({ tenantId: override.tenantId });
                        }}
                    >
                        Revert to default
                    </Button>
                </td>
            </tr>
            {error && (
                <tr className="border-t border-line-subtle">
                    <td colSpan={showRollout ? 5 : 4} className="px-5 py-2.5">
                        <WriteError error={error} />
                    </td>
                </tr>
            )}
        </>
    );
}
